import { useState, useEffect, useRef } from 'react';
import {
  IconAlertCircle,
  IconAdjustmentsHorizontal,
  IconArrowLeft,
  IconCheck,
} from '@tabler/icons-react';
import TipBlue from '../Utils/TipBlue';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';
const GROUP_GRID = 'grid-cols-1 min-[640px]:grid-cols-2';
const CARD_SHADOW = '0 12px 32px rgba(0, 0, 0, 0.28)';

function formatPrice(n) {
  return '$' + (Number(n) || 0).toFixed(2);
}

function StateBlock({ icon: Icon, title, description, tone = 'neutral' }) {
  const isError = tone === 'error';

  return (
    <div
      className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center"
      role={isError ? 'alert' : 'status'}
    >
      <Icon
        size={26}
        color={isError ? '#ff453a' : 'rgba(255, 255, 255, 0.28)'}
        stroke={1.25}
      />
      <p
        className="m-0"
        style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
      >
        {title}
      </p>
      {description && (
        <p
          className="m-0"
          style={{
            fontSize: '13px',
            lineHeight: '19px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.45)',
            fontFamily: FONT,
          }}
        >
          {description}
        </p>
      )}
    </div>
  );
}

function GroupsSkeleton() {
  return (
    <div className={`grid gap-3 lg:gap-4 ${GROUP_GRID}`} role="status" aria-label="Cargando variantes">
      {[0, 1].map((group) => (
        <div
          key={group}
          className="rounded-3xl p-3 lg:p-5"
          style={{ backgroundColor: '#141414', boxShadow: CARD_SHADOW }}
        >
          <div
            className="h-4 w-24 rounded-full animate-pulse"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.07)' }}
          />
          <div className="flex flex-col gap-1.5 mt-4">
            {[0, 1, 2].map((option) => (
              <div
                key={option}
                className="h-12 rounded-2xl animate-pulse"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PersonalizarItem({
  product,
  initialSelection = [],
  isEditing = false,
  hideTip = false,
  onBack,
  onConfirm,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState({});

  const initialSelectionRef = useRef(initialSelection);
  useEffect(() => {
    initialSelectionRef.current = initialSelection;
  }, [initialSelection]);

  const selectionKey = JSON.stringify(initialSelection);

  useEffect(() => {
    if (!product) return;
    let cancelled = false;

    setLoadError(false);
    setLoading(true);

    apiFetch(`/variants/by-item/${product.id}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const preselect = {};
    initialSelectionRef.current.forEach(({ group_id, option_id }) => {
      if (!preselect[group_id]) preselect[group_id] = [];
      preselect[group_id].push(option_id);
    });
    setSelected(preselect);
  }, [product, selectionKey]);

  if (!product) return null;

  const toggleOption = (groupId, optionId) => {
    setSelected((prev) => {
      const current = prev[groupId] || [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      return { ...prev, [groupId]: next };
    });
  };

  const selectedOptions = [];
  groups.forEach((g) => {
    (selected[g.id] || []).forEach((optId) => {
      const opt = g.options.find((o) => o.id === optId);
      if (opt) selectedOptions.push(opt);
    });
  });

  const missingRequired = groups.some(
    (g) => g.is_required && !(selected[g.id] || []).length
  );

  const basePrice = Number(product.price) || 0;
  const variantsCost = selectedOptions.reduce(
    (sum, o) => sum + (Number(o.price_delta) || 0),
    0
  );

  const handleConfirm = () => {
    const variants = [];
    groups.forEach((g) => {
      (selected[g.id] || []).forEach((optId) => {
        const opt = g.options.find((o) => o.id === optId);
        if (opt) {
          variants.push({
            group_id: g.id,
            option_id: opt.id,
            group_name: g.name,
            option_name: opt.name,
            price_delta: Number(opt.price_delta) || 0,
          });
        }
      });
    });
    onConfirm({
      optionIds: variants.map((v) => v.option_id),
      variants,
      quantity: 1,
    });
  };

  const isConfirmDisabled = loading || loadError || missingRequired;

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ fontFamily: FONT }}>
      <div className="flex items-center gap-3 md:gap-4 shrink-0 pb-5 md:pb-6">
        <button
          type="button"
          onClick={onBack}
          aria-label="Regresar al catálogo"
          title="Regresar"
          className="flex items-center justify-center shrink-0 w-12 h-12 rounded-full border-none cursor-pointer text-white transition-all duration-200 ease-out active:scale-[0.98] hover:bg-white/10"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
        >
          <IconArrowLeft size={20} stroke={2} />
        </button>

        <div
          className="flex items-center justify-center shrink-0 w-16 h-16 rounded-3xl overflow-hidden"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
        >
          <img
            src={product.img_url || '/CoffeDummy.png'}
            alt={product.name}
            className="w-12 h-12 object-contain"
          />
        </div>

        <div className="flex-1 min-w-0">
          <h2
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '19px',
              lineHeight: '25px',
              letterSpacing: '-0.01em',
              color: 'rgba(255, 255, 255, 0.95)',
            }}
            title={product.name}
          >
            {product.name}
          </h2>
          <p
            className="m-0 mt-0.5 truncate"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
            }}
          >
            Personaliza tu artículo
          </p>
        </div>

        {!hideTip && <TipBlue style={{ height: '44px', padding: '0 16px' }} />}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll pr-1 pb-4" aria-busy={loading}>
        {loading ? (
          <GroupsSkeleton />
        ) : loadError ? (
          <StateBlock
            icon={IconAlertCircle}
            title="No se pudieron cargar las variantes"
            description="Intenta de nuevo en unos momentos."
            tone="error"
          />
        ) : groups.length === 0 ? (
          <StateBlock
            icon={IconAdjustmentsHorizontal}
            title="Este artículo no tiene variantes"
            description="Puedes agregarlo directamente a la orden."
          />
        ) : (
          <div className={`grid gap-3 lg:gap-4 items-start ${GROUP_GRID}`}>
            {groups.map((g) => (
              <div
                key={g.id}
                className="flex flex-col min-w-0 rounded-3xl p-3 lg:p-5"
                style={{ backgroundColor: '#141414', boxShadow: CARD_SHADOW }}
              >
                <div className="flex items-baseline flex-wrap gap-x-2.5 gap-y-0.5 mb-3 min-w-0">
                  <span
                    className="font-semibold m-0 min-w-0 break-words lg:text-[15px]"
                    style={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '-0.01em',
                      color: 'rgba(255, 255, 255, 0.92)',
                    }}
                    title={g.name}
                  >
                    {g.name}
                  </span>
                  {g.is_required && (
                    <span
                      className="shrink-0"
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'rgba(248, 124, 43, 0.9)',
                      }}
                    >
                      Requerido
                    </span>
                  )}
                </div>

                {!g.options || g.options.length === 0 ? (
                  <p
                    className="m-0 py-2"
                    style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.35)' }}
                  >
                    Sin opciones disponibles.
                  </p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {g.options.map((opt) => {
                      const isSelected = (selected[g.id] || []).includes(opt.id);
                      const delta = Number(opt.price_delta) || 0;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => toggleOption(g.id, opt.id)}
                          aria-pressed={isSelected}
                          className={`group flex items-center gap-2 lg:gap-3 w-full min-h-[48px] lg:min-h-[52px] px-2 lg:px-3 py-2 rounded-2xl border-none cursor-pointer text-left transition-all duration-200 ease-out active:scale-[0.98] text-[14px] lg:text-[15px] ${
                            isSelected
                              ? 'bg-white/[0.07]'
                              : 'bg-transparent hover:bg-white/[0.035]'
                          }`}
                          style={{
                            fontWeight: 500,
                            color: isSelected
                              ? 'rgba(255, 255, 255, 0.95)'
                              : 'rgba(255, 255, 255, 0.6)',
                          }}
                        >
                          <span
                            className={`flex items-center justify-center shrink-0 w-[22px] h-[22px] lg:w-6 lg:h-6 rounded-full border-[1.5px] transition-colors duration-200 ${
                              isSelected
                                ? 'border-[#0071e3] bg-[#0071e3]'
                                : 'border-white/20 group-hover:border-white/30'
                            }`}
                          >
                            {isSelected && <IconCheck size={13} color="#fff" stroke={3} />}
                          </span>

                          <span className="flex-1 min-w-0 truncate" title={opt.name}>{opt.name}</span>

                          {delta !== 0 && (
                            <span
                              className="shrink-0 text-[12px] lg:text-[13px]"
                              style={{
                                fontWeight: 500,
                                color: delta > 0 ? 'rgba(255, 255, 255, 0.45)' : '#30d158',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {delta > 0 ? '+' : ''}
                              {formatPrice(delta)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-4 shrink-0 mt-1 px-4 py-3 md:px-5 md:py-4 rounded-3xl"
        style={{
          backgroundColor: 'rgba(20, 20, 20, 0.72)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
        }}
      >
        <div className="min-w-0">
          <span
            className="block"
            style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}
          >
            Total
          </span>
          <div className="flex items-end gap-3 flex-wrap">
            <span
              className="block font-semibold"
              style={{
                fontSize: '28px',
                lineHeight: '32px',
                color: 'rgba(255, 255, 255, 0.95)',
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatPrice(basePrice)}
            </span>
            {variantsCost !== 0 && (
              <span
                className="pb-1"
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.45)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {variantsCost > 0 ? '+' : '−'} {formatPrice(Math.abs(variantsCost))}
                </span>{' '}
                en variantes
              </span>
            )}
          </div>
          {!loading && !loadError && missingRequired && (
            <span
              className="block mt-1"
              style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(248, 124, 43, 0.9)' }}
              aria-live="polite"
            >
              Completa los grupos requeridos.
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          title={missingRequired ? 'Completa los grupos requeridos' : undefined}
          className="flex items-center justify-center gap-2 h-12 px-7 rounded-full border-none font-semibold text-white transition-all duration-200 ease-out active:scale-[0.98] hover:brightness-110"
          style={{
            backgroundColor: '#0071e3',
            fontSize: '15px',
            letterSpacing: '-0.01em',
            opacity: isConfirmDisabled ? 0.35 : 1,
            cursor: isConfirmDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <IconCheck size={17} stroke={2.5} />
          {isEditing ? 'Guardar' : 'Agregar'}
        </button>
      </div>
    </div>
  );
}
